import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { GetMarketplaceJobsResponseDto } from "@workspace/dto/job/get-marketplace-jobs";
import { Shell } from "@workspace/ui/components/shell";
import type { AxiosResponse } from "axios";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { createSerializer, useQueryStates } from "nuqs";
import { useEffect, useMemo, useRef } from "react";
import { getLayout } from "@/components/layouts/site";
import { JobListingsLayout } from "@/components/marketplace/job-listings-layout";
import { marketplaceSearchParamsParser } from "@/components/marketplace/marketplace-search-params";
import { NEARBY_RADIUS } from "@/config/constants";
import { axios } from "@/lib/axios";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { useMinimumVisibleLoading } from "@/lib/hooks/use-minimum-visible-loading";
import { showBackgroundQueryErrorToast } from "@/lib/query-client";
import { queryKeys } from "@/lib/query-keys";

export default function MarketplacePage() {
  const router = useRouter();
  const [searchParams] = useQueryStates(marketplaceSearchParamsParser);

  const serialize = useMemo(
    () => createSerializer(marketplaceSearchParamsParser),
    []
  );

  // Create debounced search params to reduce API calls
  // This prevents API calls on every filter change and only fires after 500ms of no changes
  const debouncedSearchParams = useDebounce(
    useMemo(() => {
      // Only include non-null, non-empty values
      const filteredParams: Record<string, unknown> = {};

      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              filteredParams[key] = value;
            }
          } else {
            filteredParams[key] = value;
          }
        }
      });

      return filteredParams;
    }, [searchParams]),
    500 // 500ms delay
  );

  const lastJobsRef = useRef<GetMarketplaceJobsResponseDto["jobs"] | undefined>(
    undefined
  );

  const { data, error, isFetching, isPlaceholderData } = useQuery({
    queryKey: queryKeys.marketplace.nearby(debouncedSearchParams),
    queryFn: async () => {
      // Remove radius from the serializer input
      const query = serialize(debouncedSearchParams);

      if (query === "") {
        return [];
      }

      // Add radius manually to the query string
      const queryWithRadius = query.includes("?")
        ? `${query}&radius=${NEARBY_RADIUS}`
        : `?radius=${NEARBY_RADIUS}${query}`;

      const {
        data: { jobs },
      } = await axios.get<
        GetMarketplaceJobsResponseDto,
        AxiosResponse<GetMarketplaceJobsResponseDto>
      >(`/jobs/get-marketplace-jobs${queryWithRadius}`);
      return jobs;
    },
    enabled: router.isReady,
    placeholderData: keepPreviousData,
    // // Add caching configuration
    // staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    // gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    // refetchOnWindowFocus: false, // Don't refetch when window regains focus
    // refetchOnMount: false, // Don't refetch when component remounts
    // // Additional optimizations
    // refetchOnReconnect: false, // Don't refetch on network reconnect
    // retry: 1, // Only retry once on failure
    // retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  useEffect(() => {
    if (error) {
      showBackgroundQueryErrorToast(error);
    }
  }, [error]);

  if (data) {
    lastJobsRef.current = data;
  }

  const jobsForMap = data ?? lastJobsRef.current;
  const wantsJobsLoading =
    isFetching && (data === undefined || isPlaceholderData);
  const showJobsLoading = useMinimumVisibleLoading(wantsJobsLoading);

  return (
    <>
      <NextSeo
        canonical="https://www.vacwork.app/job-listings"
        description="Browse flexible jobs and services near you. Find piecemeal work, part-time gigs, and full-time opportunities on VacWork's marketplace."
        key="title"
        title="Job Listings | VacWork"
      />
      <Shell
        as="div"
        className="flex min-h-0 flex-1 flex-col gap-0 p-0"
        variant="sidebar"
      >
        <h1 className="sr-only">Jobs</h1>
        <div className="relative z-20 w-full shrink-0 border-border border-b bg-secondary px-4 py-2 text-center text-foreground text-xs sm:text-sm">
          Pan the map to explore jobs nearby. New listings are added daily.
        </div>
        <div className="relative min-h-0 flex-1">
          {router.isReady && (
            <JobListingsLayout jobs={jobsForMap} loading={showJobsLoading} />
          )}
        </div>
      </Shell>
    </>
  );
}

MarketplacePage.getLayout = getLayout;
